import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, CheckCircle2 } from "lucide-react";
import { submitLead } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import Spinner from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const initial = { name: "", company: "", phone: "", email: "", interest: "Uudet verkkosivut", message: "", website: "" };

export default function ContactForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Anna nimesi";
    if (!form.phone.trim()) e.phone = "Anna puhelinnumero";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Anna kelvollinen sähköposti";
    if (!form.interest) e.interest = "Valitse kiinnostuksen kohde";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitLead(form);
      trackEvent("generate_lead", {
        interest: form.interest,
        page_path: window.location.pathname + window.location.hash,
      });
      setDone(true);
      setForm(initial);
      toast.success("Kiitos yhteydenotosta! Olen sinuun yhteydessä 24 h sisällä.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Lähetys epäonnistui. Yritä uudelleen.";
      toast.error(typeof detail === "string" ? detail : "Lähetys epäonnistui.");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section className="md-section" id="yhteys" data-testid="contact-section">
      <div className="md-container">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            <span className="md-eyebrow">Yhteydenotto</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
              Varaa maksuton <span className="text-[#00E5FF]">15 min</span> kartoituspuhelu.
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed">
              Käydään läpi tarpeesi ja saat konkreettisen ehdotuksen. Ei sitoutumista, ei myyntipainetta.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href="tel:+358505531110"
                className="md-card p-5 flex items-center gap-4 hover:border-[#0066FF]/40 transition-colors"
                data-testid="contact-phone-link"
                data-track="contact-phone"
              >
                <div className="h-11 w-11 rounded-xl bg-[#0066FF]/15 border border-[#0066FF]/30 grid place-items-center text-[#00E5FF]">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-xs text-white/50 uppercase tracking-widest font-mono">Puhelin</div>
                  <div className="text-white font-medium">+358 50 553 1110</div>
                </div>
              </a>
              <a
                href="mailto:info@myyntidrive.fi"
                className="md-card p-5 flex items-center gap-4 hover:border-[#0066FF]/40 transition-colors"
                data-testid="contact-email-link"
                data-track="contact-email"
              >
                <div className="h-11 w-11 rounded-xl bg-[#0066FF]/15 border border-[#0066FF]/30 grid place-items-center text-[#00E5FF]">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="text-xs text-white/50 uppercase tracking-widest font-mono">Sähköposti</div>
                  <div className="text-white font-medium">info@myyntidrive.fi</div>
                </div>
              </a>
              <p className="text-sm text-white/60 pl-1">
                Vastaan henkilökohtaisesti <span className="text-white">24 tunnin sisällä.</span>
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-3"
          >
            {done ? (
              <div className="md-card p-10 text-center" data-testid="contact-success">
                <div className="mx-auto h-14 w-14 rounded-full bg-[#0066FF]/20 border border-[#0066FF]/40 grid place-items-center text-[#00E5FF]">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="mt-6 font-display text-2xl text-white">Kiitos yhteydenotosta!</h3>
                <p className="mt-3 text-white/70 max-w-md mx-auto">
                  Vastaan sinulle henkilökohtaisesti 24 tunnin sisällä. Sait myös vahvistuksen sähköpostiisi.
                </p>
                <button
                  type="button"
                  onClick={() => setDone(false)}
                  className="mt-6 md-btn-secondary"
                  data-testid="contact-send-another"
                >
                  Lähetä uusi viesti
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="md-card p-6 md:p-8 space-y-5" data-testid="contact-form" noValidate>
                {/* Honeypot – invisible to humans, filled by bots */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
                  <label htmlFor="md-website">Verkkosivu (jätä tyhjäksi)</label>
                  <input
                    id="md-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={update("website")}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-white/80 mb-2 block">Nimi *</Label>
                    <Input
                      value={form.name}
                      onChange={update("name")}
                      className="bg-[#0B0D10] border-white/10 text-white h-11"
                      placeholder="Etunimi Sukunimi"
                      data-testid="contact-name"
                    />
                    {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <Label className="text-white/80 mb-2 block">Yritys</Label>
                    <Input
                      value={form.company}
                      onChange={update("company")}
                      className="bg-[#0B0D10] border-white/10 text-white h-11"
                      placeholder="Yrityksesi nimi"
                      data-testid="contact-company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-white/80 mb-2 block">Puhelin *</Label>
                    <Input
                      value={form.phone}
                      onChange={update("phone")}
                      className="bg-[#0B0D10] border-white/10 text-white h-11"
                      placeholder="+358 40 000 0000"
                      data-testid="contact-phone"
                    />
                    {errors.phone && <p className="text-xs text-red-400 mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <Label className="text-white/80 mb-2 block">Sähköposti *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className="bg-[#0B0D10] border-white/10 text-white h-11"
                      placeholder="nimi@yritys.fi"
                      data-testid="contact-email"
                    />
                    {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div>
                  <Label className="text-white/80 mb-3 block">Kiinnostuksen kohde *</Label>
                  <RadioGroup
                    value={form.interest}
                    onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}
                    className="grid grid-cols-1 sm:grid-cols-3 gap-3"
                    data-testid="contact-interest"
                  >
                    {["Uudet verkkosivut", "Nykyisten sivujen uudistus", "Ylläpito / muu"].map((opt) => (
                      <label
                        key={opt}
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                          form.interest === opt
                            ? "border-[#0066FF]/60 bg-[#0066FF]/10"
                            : "border-white/10 bg-[#0B0D10] hover:border-white/25"
                        }`}
                      >
                        <RadioGroupItem value={opt} id={`opt-${opt}`} className="border-white/30" data-testid={`contact-interest-${opt.split(" ")[0].toLowerCase()}`} />
                        <span className="text-white text-sm">{opt}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <Label className="text-white/80 mb-2 block">Viesti</Label>
                  <Textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    className="bg-[#0B0D10] border-white/10 text-white"
                    placeholder="Kerro lyhyesti tarpeestasi..."
                    data-testid="contact-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="md-btn-primary w-full justify-center text-base disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                  data-track="contact-submit"
                >
                  {submitting ? (
                    <Spinner size="sm" label="Lähetetään..." data-testid="contact-submit-spinner" />
                  ) : (
                    "Lähetä yhteydenottopyyntö"
                  )}
                </button>
                <p className="text-xs text-white/50 text-center leading-relaxed" data-testid="contact-privacy-note">
                  Lähettämällä lomakkeen hyväksyt tietojesi käsittelyn{" "}
                  <a href="/tietosuoja" className="text-[#00E5FF] hover:text-white underline underline-offset-2">tietosuojaselosteen</a>{" "}
                  mukaisesti.
                </p>
                <p className="text-xs text-white/50 text-center">
                  Vastaus 24 h sisällä · Maksuton · Ei sitoutumista
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
